import React, { useMemo } from 'react';

import uniqueId from 'lodash/uniqueId';

import { classnames, spacing, typography } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import { Group } from '@lumapps/groups/types';
import { LanguageSelectorMultiple } from '@lumapps/languages/components/LanguageSelectorMultiple/LanguageSelectorMultiple';
import { TranslatableTextFieldProps } from '@lumapps/languages/components/TranslatableTextField/TranslatableTextField';
import { LANGUAGES } from '@lumapps/languages/keys';
import { URLTextField } from '@lumapps/lumx-inputs/components/URLTextField';
import { DisclosedList } from '@lumapps/lumx-lists/components/DisclosedList/DisclosedList';
import { Icon as IconType } from '@lumapps/lumx/api/icons';
import { mdiClose, mdiCodeTags, mdiImage, mdiTranslate } from '@lumapps/lumx/icons';
import {
    Dialog,
    Toolbar,
    DialogProps,
    Size,
    Button,
    Emphasis,
    Message,
    MessageProps,
    Kind,
    IconButton,
    TextField,
    Typography,
    Checkbox,
} from '@lumapps/lumx/react';
import { isPageNode, isSystemPageNode } from '@lumapps/navigation/types';
import type { NavigationMapItem } from '@lumapps/navigation/types';
import { IconPicker } from '@lumapps/pickers/components/IconPicker';
import { ADVANCED_STYLES } from '@lumapps/style/keys';
import { useTranslate, GLOBAL, getKeyForLanguage } from '@lumapps/translations';
import { filterValues } from '@lumapps/utils/object/filterValues';
import { isURLValid } from '@lumapps/utils/string/isUrl';

import { PickerTypes } from '../../constants';
import { useNavigationAdminConfiguration } from '../../context/NavigationAdminConfigurationContext';
import {
    useNavigationAdminVisibleByReference,
    useNavigationAdminVisibleByReferenceDispatch,
} from '../../context/NavigationAdminVisibleByReferenceContext';
import { reducer, initialState, actions, initState } from '../../ducks/addNavigationItemSlice';
import { NAVIGATION_ADMIN } from '../../keys';
import { AvailableNavigationItemFields, AVAILABLE_FIELDS_IN_MORE_SECTION } from '../../types';
import { getNavigationItemModalContentKeys, isNavigationItemValid } from '../../utils';
import { NavigationItemDetails } from '../NavigationItemDetails';
import { TitleField } from './Fields/TitleField';
import { VisibilitySection } from './Fields/VisibilitySection';

import './index.scss';

export interface NavigationItemModalProps {
    /** whether the modal is open or not */
    isOpen: boolean;
    /** reference to the button or element that has opened the modal */
    buttonRef?: DialogProps['parentElement'];
    /** callback when closing the modal */
    onClose: () => void;
    /** list of languages for the platform */
    languages: TranslatableTextFieldProps['languages'];
    /** Language input of the current user */
    defaultLanguage: TranslatableTextFieldProps['defaultLanguage'];
    /** default language for the instance */
    defaultInstanceLanguage: TranslatableTextFieldProps['defaultLanguage'];
    /** placeholder to be used for the title field */
    titlePlaceholder?: string;
    /** Fields to display */
    displayedFields?: { [key in AvailableNavigationItemFields]?: boolean };
    /** navigation item to edit */
    navigationItem?: NavigationMapItem;
    /** The type of picker to use. If undefined, will use the page reference type. */
    pickerType?: PickerTypes;
    /** callback to be executed when the navigation item is saved */
    onSave: (navItem: NavigationMapItem) => void;
    /** Message to be displayed at the top of the modal window  */
    message?: MessageProps['children'];
    /** The group All for visibility management */
    feedAll: Group;
    /** whether we are editing an existing navigation item or not. */
    isEdit?: boolean;
    /** scope for tracking purposes */
    scope: string;
}

const CLASSNAME = 'navigation-item-modal';

/** Edit Navigation modal or add new item modal (after a resource has been selected) */
export const NavigationItemModal: React.FC<NavigationItemModalProps> = ({
    isOpen,
    buttonRef,
    onClose,
    defaultLanguage,
    defaultInstanceLanguage,
    languages,
    titlePlaceholder,
    displayedFields: displayedFieldsProps = {},
    isEdit = false,
    navigationItem = {},
    onSave,
    message,
    feedAll,
    scope,
    pickerType,
}) => {
    // Get the NavigationMap settings from Context defined in parent
    const visibleByGroupReferences = useNavigationAdminVisibleByReference();
    const addVisibleByGroupReferences = useNavigationAdminVisibleByReferenceDispatch();

    const { availableEditableNodeFields, validateExternalUrl } = useNavigationAdminConfiguration();

    /**
     * The default language used for translatableTextField components. If the user current language is not available,
     * replace it with the instance default language
     */
    const translatableTextFieldDefaultLanguage = languages.includes(defaultLanguage)
        ? defaultLanguage
        : defaultInstanceLanguage;

    /** Merge the configuration given through props and context */
    const displayedFields = useMemo(
        () => ({ ...availableEditableNodeFields, ...displayedFieldsProps }),
        [availableEditableNodeFields, displayedFieldsProps],
    );

    /** If the nodeTitle is used instead of the title, use this field instead */
    const titleField = displayedFields.nodeTitle ? 'nodeTitle' : 'title';
    const titleFieldValues = React.useMemo(
        () => (displayedFields.nodeTitle ? navigationItem.nodeTitle : navigationItem.title),
        [displayedFields.nodeTitle, navigationItem.nodeTitle, navigationItem.title],
    );

    /**
     * If node has a linked resource, some specific behaviors happens:
     * - Display the preview block
     * - Wording changes for title helper and visibility label and helper
     * - ALL feed is hidden in the visibility field
     */
    const nodeHasResource = isPageNode(navigationItem) || isSystemPageNode(navigationItem);

    const { translateKey } = useTranslate();
    const [state, dispatch] = React.useReducer(
        reducer,
        initState(
            {
                ...initialState,
                navigationMapItem: navigationItem,
                nodeHasResource,
                feedAll,
            },
            visibleByGroupReferences,
        ),
    );
    const { get } = useDataAttributes(scope);
    const labelId = React.useMemo(() => uniqueId(`${CLASSNAME}-label-`), []);
    const descriptionId = React.useMemo(() => uniqueId(`${CLASSNAME}-description-`), []);

    /**
     * The label used for the title field
     * Not really sure that this is the best approach, there might be some languages where it is different
     * TODO: see if there are any feedbacks on this translation and create a key if necessary.
     */
    const titleFieldLabel = `${
        titlePlaceholder || translateKey(NAVIGATION_ADMIN.NAVIGATION_ITEM_TITLE)
    } (${translateKey(getKeyForLanguage(translatableTextFieldDefaultLanguage))})`;

    const onModalClose = () => {
        onClose();
        dispatch(actions.reset());
    };

    /** Remove values that are filled but that don't exist at platform level anymore. */
    React.useEffect(() => {
        const translations = titleFieldValues?.translations;
        const filteredTranslations = translations
            ? filterValues((translation, key) => languages.includes(key) && Boolean(translation), translations)
            : [];

        dispatch(
            actions.updateNavigationMapItem({
                [titleField]: { ...titleFieldValues, translations: filteredTranslations },
            }),
        );
    }, [displayedFields.nodeTitle, languages, titleField, titleFieldValues]);

    /**
     * As the dialog can have many conditional things
     * we need to dynamically set the element to focus on dialog open.
     *
     * The rule to manage the focus is the following:
     *
     * * If a content is set at the top of the dialog (on edit), set the focus on the content so that
     * screen readers can see it.
     *
     * * If no content is set, focus the first interactive element.
     */
    const firstElementRef = React.useRef(null);

    const [preventAutoClose, setPreventAutoClose] = React.useState(false);

    // Whether we need to display the "More settings" button.
    const hasMore = AVAILABLE_FIELDS_IN_MORE_SECTION.some((field: AvailableNavigationItemFields) =>
        Boolean(displayedFields[field]),
    );
    const [isExpanded, setIsExpanded] = React.useState(false);

    const onModalSave = () => {
        onClose();
        const titleValues = state.navigationMapItem[titleField];
        const titleTranslations = titleValues?.translations || {};

        // Remove all empty strings from translations
        const cleanedTranslations = filterValues(
            (translation, key) => languages.includes(key) && Boolean(translation),
            titleTranslations,
        );

        if (navigationItem) {
            // On save, we add in the visibleByGroupReference dict the new groups visibility
            addVisibleByGroupReferences(
                state.visibleBy?.reduce(
                    (acc, by) =>
                        by.id && by.name
                            ? {
                                  ...acc,
                                  [by.id]: by.name,
                              }
                            : acc,
                    {},
                ) || {},
            );

            onSave({
                ...navigationItem,
                ...state.navigationMapItem,
                [titleField]: {
                    ...titleValues,
                    translations: cleanedTranslations,
                },
                /**
                 * when custom id is modified to an empty string we need to set it as undefined so the backend provides
                 * an id.
                 */
                properties: state.navigationMapItem.properties
                    ? {
                          ...state.navigationMapItem.properties,
                          id: state.navigationMapItem.properties?.id || undefined,
                          class: state.navigationMapItem.properties?.class || undefined,
                      }
                    : undefined,
            });
        }
    };

    const onUrlBlur = () => {
        dispatch(actions.setFieldError({ field: 'url', hasError: state.touched.url && !state.navigationMapItem.url }));
    };

    const contentDependantKeys = getNavigationItemModalContentKeys({
        type: pickerType || navigationItem.pageReference?.type,
        isEdit,
    });

    return (
        <Dialog
            dialogProps={{ 'aria-labelledby': labelId, 'aria-describedby': descriptionId }}
            isOpen={isOpen}
            parentElement={buttonRef}
            onClose={onModalClose}
            size={Size.regular}
            className={CLASSNAME}
            focusElement={firstElementRef?.current ? firstElementRef : undefined}
            preventAutoClose={preventAutoClose}
        >
            <header>
                <Toolbar
                    label={
                        <h2 id={labelId} className="lumx-typography-title">
                            {translateKey(
                                isEdit ? NAVIGATION_ADMIN.EDIT_NAVIGATION_ITEM : NAVIGATION_ADMIN.ADD_NAVIGATION_ITEM,
                            )}
                        </h2>
                    }
                    after={
                        <IconButton
                            className="lumx-spacing-margin-right-regular"
                            emphasis={Emphasis.low}
                            icon={mdiClose}
                            onClick={onModalClose}
                            label={translateKey(GLOBAL.CLOSE)}
                        />
                    }
                />
            </header>

            <DisclosedList
                isExpanded={isExpanded}
                toggleExpanded={() => {
                    setIsExpanded(!isExpanded);
                }}
                hasMore={hasMore}
                toggleButtonLabel={translateKey(isExpanded ? GLOBAL.LESS_SETTINGS : GLOBAL.MORE_SETTINGS)}
                toggleButtonProps={{ className: `${CLASSNAME}__more-settings` }}
            >
                <div ref={nodeHasResource ? firstElementRef : undefined} className={`${CLASSNAME}__content`}>
                    {nodeHasResource && (
                        <>
                            {contentDependantKeys.contentBlockTitle && (
                                <h3 className={typography(Typography.subtitle1)}>
                                    {translateKey(contentDependantKeys.contentBlockTitle)}
                                </h3>
                            )}
                            {/* In this case, we want to always display the pageReference details and not the navigation's */}
                            <NavigationItemDetails node={navigationItem} displayPageReferenceData />
                        </>
                    )}

                    {message ? (
                        <>
                            <h3 className={typography(Typography.subtitle1)}>{translateKey(GLOBAL.INFORMATION)}</h3>
                            <Message
                                id={descriptionId}
                                kind={Kind.warning}
                                hasBackground
                                className="lumx-spacing-margin-bottom"
                            >
                                {message}
                            </Message>
                        </>
                    ) : null}

                    {displayedFields.url ? (
                        <URLTextField
                            /** Focus if no other previous interactive element is set */
                            inputRef={!nodeHasResource && firstElementRef}
                            isRequired
                            url={state.navigationMapItem.url}
                            onUrlChange={(url) => {
                                dispatch(actions.updateNavigationMapItem({ url }));
                            }}
                            urlValidator={(value) => (validateExternalUrl ? isURLValid(value) : true)}
                            onBlur={onUrlBlur}
                            hasError={state.errors.url}
                            error={state.errors.url ? translateKey(GLOBAL.ERROR_MANDATORY_FIELD) : undefined}
                            {...get({
                                element: 'url-field',
                            })}
                        />
                    ) : null}

                    {displayedFields.title || displayedFields.nodeTitle ? (
                        <TitleField
                            fieldToEdit={titleField}
                            state={state}
                            dispatch={dispatch}
                            languages={languages}
                            defaultLanguage={translatableTextFieldDefaultLanguage}
                            label={titleFieldLabel}
                            /** Focus if no other previous interactive element is set */
                            inputRef={
                                (!nodeHasResource && !displayedFields.url && firstElementRef) as
                                    | React.RefObject<HTMLInputElement>
                                    | undefined
                            }
                            scope={scope}
                            helper={translateKey(contentDependantKeys.titleHelper)}
                        />
                    ) : null}

                    {displayedFields.visibility ? (
                        <VisibilitySection
                            state={state}
                            dispatch={dispatch}
                            className={CLASSNAME}
                            focusOnInit={Boolean(
                                !firstElementRef?.current &&
                                    !nodeHasResource &&
                                    !displayedFields.url &&
                                    !displayedFields.title &&
                                    !displayedFields.nodeTitle,
                            )}
                            helper={contentDependantKeys.visibilityHelper}
                            nodeHasResource={nodeHasResource}
                            setPreventAutoClose={setPreventAutoClose}
                        />
                    ) : null}

                    {/** ******* More settings ******** */}

                    {isExpanded && (
                        <>
                            {displayedFields.icon ? (
                                <>
                                    <IconPicker
                                        label={translateKey(GLOBAL.ICON)}
                                        icon={navigationItem.icon}
                                        onChange={(icon: IconType | null) => {
                                            dispatch(
                                                actions.updateNavigationMapItem({
                                                    icon: icon?.name,
                                                    hideTitle:
                                                        icon === null ? undefined : state.navigationMapItem.hideTitle,
                                                }),
                                            );
                                        }}
                                        selectProps={{ icon: state.navigationMapItem.icon ? undefined : mdiImage }}
                                    />

                                    <Checkbox
                                        label={translateKey(NAVIGATION_ADMIN.HIDE_TITLE_IN_MAIN_NAVIGATION)}
                                        helper={translateKey(GLOBAL.SELECT_AN_ICON_TO_MAKE_THIS_OPTION_AVAILABLE)}
                                        isChecked={Boolean(state.navigationMapItem.hideTitle)}
                                        isDisabled={!state.navigationMapItem.icon}
                                        onChange={(isChecked: boolean) => {
                                            dispatch(
                                                actions.updateNavigationMapItem({
                                                    hideTitle: isChecked,
                                                }),
                                            );
                                        }}
                                    />
                                </>
                            ) : null}

                            {displayedFields.openInNewTab ? (
                                <Checkbox
                                    label={translateKey(GLOBAL.OPEN_NEW_TAB)}
                                    className={classnames(
                                        spacing('margin', 'top', 'huge'),
                                        spacing('margin', 'bottom', 'big'),
                                    )}
                                    isChecked={Boolean(state.navigationMapItem.newTab)}
                                    onChange={(isChecked: boolean) => {
                                        dispatch(
                                            actions.updateNavigationMapItem({
                                                newTab: isChecked,
                                            }),
                                        );
                                    }}
                                />
                            ) : null}

                            {displayedFields.displayOnlyInLanguage ? (
                                <LanguageSelectorMultiple
                                    className={`${CLASSNAME}__display-only`}
                                    selected={state.navigationMapItem.langs}
                                    selectProps={{
                                        label: translateKey(LANGUAGES.DISPLAY_ONLY_FOR_LANGUAGES),
                                        icon: mdiTranslate,
                                        helper: translateKey(NAVIGATION_ADMIN.DISPLAY_ONLY_FOR_LANGUAGES_DESCRIPTION),
                                    }}
                                    languages={languages}
                                    onChange={(languages) =>
                                        dispatch(
                                            actions.updateNavigationMapItem({
                                                langs: languages,
                                            }),
                                        )
                                    }
                                />
                            ) : null}

                            {displayedFields.useAsSubNavigation ? (
                                <fieldset
                                    className={classnames(`${CLASSNAME}__fieldset`, spacing('margin', 'top', 'big'))}
                                >
                                    <legend className={typography(Typography.subtitle1)}>
                                        {translateKey(NAVIGATION_ADMIN.USE_AS_SUB_NAV_TITLE)}
                                    </legend>
                                    <Message
                                        id={descriptionId}
                                        kind={Kind.info}
                                        hasBackground
                                        className={spacing('margin', 'top', 'big')}
                                    >
                                        {translateKey(NAVIGATION_ADMIN.USE_AS_SUB_NAV_DESCRIPTION)}
                                    </Message>
                                    <Checkbox
                                        label={translateKey(NAVIGATION_ADMIN.HIDE_IN_MAIN_NAV)}
                                        helper={translateKey(NAVIGATION_ADMIN.HIDE_IN_MAIN_NAV_HELPER)}
                                        className={spacing('margin', 'top', 'big')}
                                        isChecked={Boolean(state.navigationMapItem.isHidden)}
                                        onChange={(isChecked: boolean) => {
                                            dispatch(
                                                actions.updateNavigationMapItem({
                                                    isHidden: isChecked,
                                                    areChildrenHidden: isChecked
                                                        ? false
                                                        : state.navigationMapItem.areChildrenHidden,
                                                }),
                                            );
                                        }}
                                    />
                                    <Checkbox
                                        label={translateKey(NAVIGATION_ADMIN.HIDE_CHILDREN_IN_MAIN_NAV)}
                                        className={classnames(
                                            spacing('margin', 'top', 'big'),
                                            spacing('margin', 'bottom', 'big'),
                                        )}
                                        isDisabled={state.navigationMapItem.isHidden}
                                        isChecked={Boolean(state.navigationMapItem.areChildrenHidden)}
                                        onChange={(isChecked: boolean) => {
                                            dispatch(
                                                actions.updateNavigationMapItem({
                                                    areChildrenHidden: isChecked,
                                                }),
                                            );
                                        }}
                                    />
                                </fieldset>
                            ) : null}

                            {displayedFields.customCode ? (
                                <fieldset
                                    className={classnames(`${CLASSNAME}__fieldset`, spacing('margin', 'top', 'big'))}
                                >
                                    <legend className={typography(Typography.subtitle1)}>
                                        {translateKey(ADVANCED_STYLES.CUSTOM_CODE)}
                                    </legend>
                                    <TextField
                                        value={state.navigationMapItem.properties?.class}
                                        onChange={(newValue: string) => {
                                            dispatch(
                                                actions.updateNavigationMapItem({
                                                    properties: {
                                                        ...state.navigationMapItem.properties,
                                                        class: newValue,
                                                    },
                                                }),
                                            );
                                        }}
                                        icon={mdiCodeTags}
                                        label={translateKey(NAVIGATION_ADMIN.CUSTOM_CODE_ITEM_CLASSES_TITLE)}
                                        helper={translateKey(NAVIGATION_ADMIN.CUSTOM_CODE_ITEM_CLASSES_HELPER)}
                                    />

                                    <TextField
                                        value={state.navigationMapItem.properties?.id}
                                        icon={mdiCodeTags}
                                        onChange={(newValue: string) => {
                                            dispatch(
                                                actions.updateNavigationMapItem({
                                                    properties: {
                                                        ...state.navigationMapItem.properties,
                                                        id: newValue,
                                                    },
                                                }),
                                            );
                                        }}
                                        label={translateKey(NAVIGATION_ADMIN.CUSTOM_CODE_ITEM_IDENTIFIER_TITLE)}
                                    />
                                </fieldset>
                            ) : null}
                        </>
                    )}
                </div>
            </DisclosedList>

            <footer>
                <Toolbar
                    after={
                        <>
                            <Button emphasis={Emphasis.medium} onClick={onModalClose}>
                                {translateKey(GLOBAL.CANCEL)}
                            </Button>
                            <Button
                                className="lumx-spacing-margin-left-regular"
                                onClick={onModalSave}
                                isDisabled={!isNavigationItemValid(state.navigationMapItem, { validateExternalUrl })}
                            >
                                {translateKey(isEdit ? GLOBAL.DONE : GLOBAL.ADD)}
                            </Button>
                        </>
                    }
                />
            </footer>
        </Dialog>
    );
};
